import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { trackEvent } from '../../services/analytics';
import {
  useDiceCreateCheckout,
  useDiceSubscriptions,
  useDiceCancelPending,
} from '../../hooks/useApi';
import { Button } from '../../components/base-components';
import { PageShell } from '../components/PageShell';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { Skeleton } from '../components/Skeleton';
import { cx, FOCUS_RING } from '../components/styles';
import { v2Path } from '../nav';

/* ------------------------------------------------------------------ *
 * Logic block copied verbatim from src/pages/DiceSignupPage.tsx.
 * Only the JSX and the className strings are new, plus the three internal
 * paths which now route through v2Path().
 * ------------------------------------------------------------------ */

const FIELD_CLASS =
  'w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#3b82f6]/50 focus:outline-none';

export const DiceSignupScreen: React.FC = () => {
  const { showError } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: subData, isLoading: subLoading } = useDiceSubscriptions();
  const checkoutMutation = useDiceCreateCheckout();
  const cancelPendingMutation = useDiceCancelPending();

  const [pendingSession, setPendingSession] = useState<{
    checkout_url: string;
    subscription_id: number;
    forwarding_email?: string;
    dice_email?: string;
    dice_first_name?: string;
    dice_last_name?: string;
  } | null>(null);

  const [useRandomEmail, setUseRandomEmail] = useState(false);
  const [diceEmail, setDiceEmail] = useState('');
  const [diceFirstName, setDiceFirstName] = useState('');
  const [diceLastName, setDiceLastName] = useState('');
  const [diceDob, setDiceDob] = useState('');

  const today = new Date();
  const minDob = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate())
    .toISOString()
    .slice(0, 10);
  const maxDob = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())
    .toISOString()
    .slice(0, 10);

  useEffect(() => { trackEvent('dice_signup_page_viewed'); }, []);

  const subs = subData?.subscriptions || [];
  // Find any sub that is paid but never completed setup
  const subNeedingSetup = subs.find((s) => s.is_active && !s.connection_id);
  const hasActiveSub = subs.some((s) => s.is_active);
  const needsSetup = !!subNeedingSetup;

  const handleCheckout = async () => {
    const email = user?.email || '';
    if (!email) {
      showError('Could not determine your email. Please log in again.');
      return;
    }
    if (!useRandomEmail && !diceEmail.trim()) {
      showError('Please enter a Dice email or select "Use random email"');
      return;
    }
    try {
      const data = await checkoutMutation.mutateAsync({
        forwardingEmail: email,
        diceEmail: useRandomEmail ? null : diceEmail.trim(),
        firstName: diceFirstName.trim() || undefined,
        lastName: diceLastName.trim() || undefined,
        dob: diceDob || undefined,
      });
      sessionStorage.setItem('dice_subscription_id', String(data.subscription_id));
      if (data.resumed) {
        setPendingSession({
          checkout_url: data.checkout_url,
          subscription_id: data.subscription_id,
          forwarding_email: data.forwarding_email,
          dice_email: data.dice_email,
          dice_first_name: data.dice_first_name,
          dice_last_name: data.dice_last_name,
        });
        return;
      }
      trackEvent('dice_checkout_started', { subscriptionId: String(data.subscription_id) });
      window.location.href = data.checkout_url;
    } catch (error: any) {
      showError(error?.message || 'Failed to create checkout session');
    }
  };

  const handleCancelPending = async () => {
    if (!pendingSession) return;
    try {
      await cancelPendingMutation.mutateAsync(pendingSession.subscription_id);
      setPendingSession(null);
      const email = user?.email || '';
      const data = await checkoutMutation.mutateAsync({
        forwardingEmail: email,
        diceEmail: useRandomEmail ? null : diceEmail.trim(),
        firstName: diceFirstName.trim() || undefined,
        lastName: diceLastName.trim() || undefined,
        dob: diceDob || undefined,
      });
      sessionStorage.setItem('dice_subscription_id', String(data.subscription_id));
      trackEvent('dice_checkout_started', { subscriptionId: String(data.subscription_id) });
      window.location.href = data.checkout_url;
    } catch (error: any) {
      showError(error?.message || 'Failed to cancel pending session');
    }
  };

  const breadcrumb = [
    { label: 'Dice Accounts', to: v2Path('/dice/settings') },
    { label: 'New account' },
  ];

  if (pendingSession) {
    const isCanceling = cancelPendingMutation.isPending || checkoutMutation.isPending;
    return (
      <PageShell width="narrow">
        <PageHeader title="Create Dice Account" breadcrumb={breadcrumb} />
        <div className="rounded-2xl border border-yellow-500/30 bg-[#0f0f0f] p-6">
          <h2 className="mb-1 text-lg font-medium text-white">Pending checkout found</h2>
          <p className="mb-4 text-sm text-white/50">
            You have an open checkout session with the following details. Resume this session?
          </p>
          <div className="mb-4 space-y-1 text-sm">
            {pendingSession.forwarding_email && (
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                <span className="w-32 shrink-0 text-white/40">Forwarding email</span>
                <span className="break-all text-white">{pendingSession.forwarding_email}</span>
              </div>
            )}
            {(pendingSession.dice_first_name || pendingSession.dice_last_name) && (
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                <span className="w-32 shrink-0 text-white/40">Dice name</span>
                <span className="text-white">
                  {[pendingSession.dice_first_name, pendingSession.dice_last_name]
                    .filter(Boolean)
                    .join(' ')}
                </span>
              </div>
            )}
            {pendingSession.dice_email && (
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
                <span className="w-32 shrink-0 text-white/40">Dice email</span>
                <span className="break-all text-white">{pendingSession.dice_email}</span>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              onClick={handleCancelPending}
              disabled={isCanceling}
              className={cx(
                'flex-1 rounded-xl border border-white/10 px-4 py-2.5 text-sm font-medium text-white/60 transition-colors hover:text-white disabled:opacity-50',
                FOCUS_RING,
              )}
            >
              {isCanceling ? 'Canceling...' : 'Cancel & Start Over'}
            </button>
            <button
              onClick={() => { window.location.href = pendingSession.checkout_url; }}
              disabled={isCanceling}
              className={cx(
                'flex-1 rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-black transition-colors hover:bg-white/90 disabled:opacity-50',
                FOCUS_RING,
              )}
            >
              Continue to Payment
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  if (subLoading) {
    return (
      <PageShell width="narrow">
        <PageHeader title="Create Dice Account" breadcrumb={breadcrumb} />
        <div className="space-y-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-40 w-full" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell width="narrow">
      <PageHeader
        title="Create Dice Account"
        subtitle="$1 one-time setup + $1/month for SMS forwarding"
        breadcrumb={breadcrumb}
      />

      <div className="space-y-4">
        {/* Banner: sub still setting up */}
        {needsSetup && (
          <Panel padding="sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="min-w-0 text-sm text-white/50">
                A previous account is still being set up automatically.
              </p>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  sessionStorage.setItem('dice_subscription_id', String(subNeedingSetup!.id));
                  navigate(v2Path('/dice/success'));
                }}
              >
                Check Status
              </Button>
            </div>
          </Panel>
        )}

        {/* Banner: existing active subs */}
        {hasActiveSub && !needsSetup && (
          <Panel padding="sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="min-w-0 text-sm text-white/50">
                You have {subs.filter((s) => s.is_active).length} active account
                {subs.filter((s) => s.is_active).length !== 1 ? 's' : ''}.
              </p>
              <Link to={v2Path('/dice/settings')} className={cx('rounded', FOCUS_RING)}>
                <Button variant="secondary" size="sm">Manage</Button>
              </Link>
            </div>
          </Panel>
        )}

        {/* Always show checkout */}
        <Panel>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-white/70">
              Get a dedicated phone number with SMS forwarding to create and manage your Dice
              account through FlashTools.
            </p>
            <ul className="space-y-2 text-sm text-white/60">
              <li className="flex gap-2">
                <span className="text-[#3b82f6]">1.</span>
                <span>Pay $1 setup + $1/month subscription</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3b82f6]">2.</span>
                <span>We assign you a phone number and create your Dice account automatically</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#3b82f6]">3.</span>
                <span>All SMS messages are forwarded to your email</span>
              </li>
            </ul>

            {/* Dice account profile (optional) */}
            <div className="space-y-3 border-t border-white/5 pt-2">
              <div>
                <label className="block text-xs text-white/40">Dice Account Profile</label>
                <p className="mt-0.5 text-xs text-white/40">
                  Optional. Blank fields will be auto-generated.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="space-y-1">
                  <label htmlFor="dice-first-name" className="block text-xs text-white/40">
                    First name <span className="text-white/30">(optional)</span>
                  </label>
                  <input
                    id="dice-first-name"
                    type="text"
                    value={diceFirstName}
                    onChange={e => setDiceFirstName(e.target.value)}
                    maxLength={100}
                    placeholder="Auto-generated if blank"
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="space-y-1">
                  <label htmlFor="dice-last-name" className="block text-xs text-white/40">
                    Last name <span className="text-white/30">(optional)</span>
                  </label>
                  <input
                    id="dice-last-name"
                    type="text"
                    value={diceLastName}
                    onChange={e => setDiceLastName(e.target.value)}
                    maxLength={100}
                    placeholder="Auto-generated if blank"
                    className={FIELD_CLASS}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="dice-dob" className="block text-xs text-white/40">
                  Date of birth <span className="text-white/30">(optional)</span>
                </label>
                <input
                  id="dice-dob"
                  type="date"
                  value={diceDob}
                  onChange={e => setDiceDob(e.target.value)}
                  min={minDob}
                  max={maxDob}
                  className={FIELD_CLASS}
                />
              </div>
            </div>

            {/* Dice email option */}
            <div className="space-y-2">
              <label className="flex cursor-pointer select-none items-center gap-2">
                <input
                  type="checkbox"
                  checked={useRandomEmail}
                  onChange={e => setUseRandomEmail(e.target.checked)}
                  className="h-4 w-4 cursor-pointer accent-[#3b82f6]"
                />
                <span className="text-sm text-white/60">Use random email</span>
              </label>
              {!useRandomEmail && (
                <div className="space-y-1">
                  <label htmlFor="dice-email" className="block text-xs text-white/40">
                    Dice account email
                  </label>
                  <input
                    id="dice-email"
                    type="email"
                    value={diceEmail}
                    onChange={e => setDiceEmail(e.target.value)}
                    placeholder="you@example.com"
                    className={FIELD_CLASS}
                  />
                </div>
              )}
            </div>

            <Button
              onClick={handleCheckout}
              disabled={checkoutMutation.isPending}
            >
              {checkoutMutation.isPending ? 'Loading...' : 'Continue to Payment'}
            </Button>
          </div>
        </Panel>
      </div>
    </PageShell>
  );
};

export default DiceSignupScreen;
